/**
 * =============================================================================
 * Componente: AffiliateMoreSheet
 * Versão: 1.0.0 (Redesign Premium)
 *
 * Descrição:
 *   Sheet acionado pelo item "Mais" da bottom-nav (mobile). Mostra itens
 *   secundários: Saques, Materiais, Perfil + ação de Sair.
 * =============================================================================
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { Banknote, ImageIcon, UserCircle, LogOut } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { cn } from '@/lib/utils';

interface AffiliateMoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ITEMS = [
  { to: '/afiliado/saques', label: 'Saques', icon: Banknote, hint: 'Solicitar e acompanhar' },
  { to: '/afiliado/materiais', label: 'Materiais', icon: ImageIcon, hint: 'Banners e textos prontos' },
  { to: '/afiliado/perfil', label: 'Perfil', icon: UserCircle, hint: 'Dados pessoais e PIX' },
] as const;

export function AffiliateMoreSheet({ open, onOpenChange }: AffiliateMoreSheetProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { signOut, affiliate } = useAffiliateAuth();

  const handleNavigate = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          // CRÍTICO: o SheetContent é renderizado via Portal no document.body,
          // FORA do <div className="affiliate-shell">. Sem reaplicar a classe,
          // todas as CSS vars --aff-* ficam indefinidas e o background fica
          // TRANSPARENTE — o conteúdo da página vaza por baixo do sheet e os
          // itens secundários parecem "soltos" no overlay. Reaplicar
          // 'affiliate-shell' aqui restaura o escopo dos design tokens.
          'affiliate-shell',
          'rounded-t-[28px] border-t-0 p-0 aff-safe-bottom',
          // Garante que o sheet nunca ultrapasse a viewport e tenha scroll
          // interno se o conteúdo crescer (e-mail longo, mais itens etc.).
          'max-h-[85dvh] overflow-y-auto',
          'bg-[hsl(var(--aff-surface))] text-[hsl(var(--aff-foreground))]',
        )}
      >
        {/* Drag handle */}
        <div className="mx-auto mt-3 h-1.5 w-10 rounded-full bg-[hsl(var(--aff-border))]" />

        <SheetHeader className="px-5 pt-3 pb-1 text-left">
          <SheetTitle className="text-[hsl(var(--aff-foreground))]">
            Mais opções
          </SheetTitle>
          {affiliate?.email && (
            <p className="text-xs text-[hsl(var(--aff-muted-foreground))]">
              {affiliate.email}
            </p>
          )}
        </SheetHeader>

        <ul className="px-3 pb-3 pt-2">
          {ITEMS.map((item) => {
            const active = pathname.startsWith(item.to);
            return (
              <li key={item.to}>
                <button
                  type="button"
                  onClick={() => handleNavigate(item.to)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors',
                    'hover:bg-[hsl(var(--aff-surface-2))]',
                    active && 'bg-[hsl(var(--aff-primary-soft))]',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-xl',
                      active
                        ? 'bg-[hsl(var(--aff-primary))] text-[hsl(var(--aff-primary-foreground))]'
                        : 'bg-[hsl(var(--aff-surface-2))] text-[hsl(var(--aff-primary))]',
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-[hsl(var(--aff-foreground))]">
                      {item.label}
                    </span>
                    <span className="block text-[11.5px] text-[hsl(var(--aff-muted-foreground))]">
                      {item.hint}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="px-5 pb-5">
          <Button
            variant="outline"
            className="w-full justify-start gap-2 rounded-2xl border-[hsl(var(--aff-border))] bg-transparent text-[hsl(var(--aff-danger))] hover:bg-[hsl(var(--aff-danger)/0.08)] hover:text-[hsl(var(--aff-danger))]"
            onClick={async () => {
              onOpenChange(false);
              await signOut();
            }}
          >
            <LogOut className="h-4 w-4" />
            Sair da conta
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
